import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '@/utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const accessTokenRef = useRef(null);
  const refreshInFlightRef = useRef(null);

  // Expose access token for API layer
  const getAccessToken = useCallback(() => accessTokenRef.current, []);

  const setTokens = useCallback((accessToken, userData) => {
    accessTokenRef.current = accessToken;
    if (accessToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
    setUser(userData);
    // Persist user info (NOT tokens) for UI restoration on reload
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
  }, []);

  // Refresh access token using HTTP-only cookie
  const refreshAccessToken = useCallback(async () => {
    // Deduplicate concurrent refresh calls
    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    refreshInFlightRef.current = (async () => {
      try {
        const res = await api.post('/api/auth/refresh', {}, { withCredentials: true, _skipAuthRetry: true });
        const { access_token, user: userData } = res.data;
        setTokens(access_token, userData);
        return access_token;
      } catch {
        // Refresh failed — clear session
        setTokens(null, null);
        return null;
      } finally {
        refreshInFlightRef.current = null;
      }
    })();

    return refreshInFlightRef.current;
  }, [setTokens]);

  // On mount: try to restore session via refresh token cookie
  useEffect(() => {
    const initAuth = async () => {
      // Try refresh token first (best: HTTP-only cookie)
      try {
        const res = await api.post('/api/auth/refresh', {}, { withCredentials: true, _skipAuthRetry: true });
        const { access_token, user: userData } = res.data;
        setTokens(access_token, userData);
      } catch {
        // No valid refresh token — check legacy localStorage token
        const legacyToken = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');
        if (legacyToken && savedUser) {
          api.defaults.headers.common['Authorization'] = `Bearer ${legacyToken}`;
          try {
            const meRes = await api.get('/api/auth/me', { _skipAuthRetry: true });
            accessTokenRef.current = legacyToken;
            setUser(meRes.data);
          } catch {
            // Legacy token also invalid
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete api.defaults.headers.common['Authorization'];
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [setTokens]);

  // 401 interceptor: auto-refresh and retry
  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          !originalRequest._skipAuthRetry &&
          !originalRequest._retried
        ) {
          originalRequest._retried = true;
          const newToken = await refreshAccessToken();
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return api(originalRequest);
          }
          // Refresh failed — redirect to login
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptorId);
  }, [refreshAccessToken]);

  const login = async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password }, { withCredentials: true });
    const { access_token, user: userData } = response.data;
    setTokens(access_token, userData);
    // Also store in localStorage for backward compatibility
    localStorage.setItem('token', access_token);
    return userData;
  };

  const register = async (email, password, name, role, company_name = null) => {
    const response = await api.post('/api/auth/register', { email, password, name, role, company_name }, { withCredentials: true });
    if (response.data.pending) {
      return { pending: true, message: response.data.message };
    }
    const { access_token, user: userData } = response.data;
    setTokens(access_token, userData);
    localStorage.setItem('token', access_token);
    return userData;
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout', {}, { withCredentials: true });
    } catch { /* ignore */ }
    setTokens(null, null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
